import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { INDUSTRIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", category: "consumo", description: "", website: "" };

export default function SponsorBrands() {
  const { t } = useTranslation();
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/api/brands");
    setBrands(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name.trim()) return toast.error(t("sponsor_brands.toast_name_required"));
    try {
      if (editing.id) await api.put(`/api/brands/${editing.id}`, editing);
      else await api.post("/api/brands", editing);
      setOpen(false);
      setEditing(empty);
      load();
      toast.success(t("sponsor_brands.toast_saved"));
    } catch (e) { toast.error(t("sponsor_brands.toast_save_failed")); }
  };

  const remove = async (id) => {
    await api.delete(`/api/brands/${id}`);
    load();
  };

  return (
    <DashboardLayout
      title={t("sponsor_brands.title")}
      subtitle={t("sponsor_brands.subtitle")}
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(empty)} data-testid="button-add-brand" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
              <Plus size={16} className="mr-2" /> {t("sponsor_brands.new_brand")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing.id ? t("sponsor_brands.edit_brand_title") : t("sponsor_brands.new_brand_title")}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div>
                <Label>{t("sponsor_brands.label_name")}</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="input-brand-name" />
              </div>
              <div>
                <Label>{t("sponsor_brands.label_category")}</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("sponsor_brands.label_description")}</Label>
                <Textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <Label>{t("sponsor_brands.label_website")}</Label>
                <Input value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={save} data-testid="button-save-brand" className="bg-petroleum text-white hover:bg-petroleum/90">{t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {brands.length === 0 ? (
        <div className="kpi-card p-8 text-center text-slate-500">
          {t("sponsor_brands.empty")}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {brands.map((b) => (
            <div key={b.id} className="kpi-card p-5" data-testid={`card-brand-${b.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-xl text-petroleum">{b.name}</h3>
                  <p className="text-xs uppercase tracking-widest text-gold mt-1">{b.category}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditing(b); setOpen(true); }} className="text-petroleum hover:text-gold" data-testid={`button-edit-brand-${b.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => remove(b.id)} className="text-red-600 hover:text-red-700" data-testid={`button-delete-brand-${b.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-2 line-clamp-2">{b.description}</p>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
